import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';

const VendorFormModal = ({ vendor, onClose, onSaved }) => {
  const isEdit = Boolean(vendor);

  const [form, setForm] = useState({
    name: '',
    type: 'raw_material',
    contact_person: '',
    phone: '',
    email: '',
    ntn: '',
    strn: '',
    address: '',
    payment_terms_days: 30,
    is_active: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (vendor) {
      setForm({
        name: vendor.name || '',
        type: vendor.type || 'raw_material',
        contact_person: vendor.contact_person || '',
        phone: vendor.phone || '',
        email: vendor.email || '',
        ntn: vendor.ntn || '',
        strn: vendor.strn || '',
        address: vendor.address || '',
        payment_terms_days: vendor.payment_terms_days != null ? vendor.payment_terms_days : 30,
        is_active: vendor.is_active !== undefined ? vendor.is_active : true,
      });
    }
  }, [vendor]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.name.trim() || !form.type) {
      alert('❌ Name and Type are required.');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        ...form,
        name: form.name.trim(),
        contact_person: form.contact_person.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        ntn: form.ntn.trim() || null,
        strn: form.strn.trim() || null,
        address: form.address.trim() || null,
        payment_terms_days: form.payment_terms_days === '' ? 30 : parseInt(form.payment_terms_days, 10),
      };

      if (isEdit) {
        await chemicalService.updateVendor(vendor.id, payload);
        alert('✅ Vendor updated successfully!');
      } else {
        await chemicalService.createVendor(payload);
        alert('✅ Vendor created successfully!');
      }
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save vendor';
      alert('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>
              {isEdit ? '✏️ Edit Vendor' : '➕ Add Vendor'}
            </h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              {isEdit ? `Updating ${vendor.name}` : 'Create a new vendor record'}
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px 30px' }}>

            {/* ─── Section: Business Info ─────────── */}
            <div style={styles.sectionLabel}>Business</div>
            <div style={styles.row}>
              <div style={{ ...styles.formGroup, flex: 2 }}>
                <label style={styles.label}>
                  Vendor Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. Bashir Chemicals"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Type <span style={styles.required}>*</span>
                </label>
                <select
                  value={form.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  style={styles.input}
                  required
                >
                  <option value="importer">Importer</option>
                  <option value="distributor">Distributor</option>
                  <option value="trader">Trader</option>
                </select>
              </div>
            </div>

            {/* ─── Section: Contact ───────────────── */}
            <div style={styles.sectionLabel}>Contact</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Contact Person</label>
                <input
                  type="text"
                  value={form.contact_person}
                  onChange={(e) => handleChange('contact_person', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. Bashir Ahmed"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. +92 321 8765432"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. sales@example.com"
                />
              </div>
            </div>

            <div style={{ ...styles.formGroup, marginTop: '8px' }}>
              <label style={styles.label}>Address</label>
              <textarea
                value={form.address}
                onChange={(e) => handleChange('address', e.target.value)}
                style={{ ...styles.input, minHeight: '60px', resize: 'vertical', fontFamily: 'inherit' }}
                placeholder="e.g. Korangi Industrial, Karachi"
                rows="2"
              />
            </div>

            {/* ─── Section: Tax + Terms ───────────── */}
            <div style={styles.sectionLabel}>Tax & Payment Terms</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>NTN (National Tax Number)</label>
                <input
                  type="text"
                  value={form.ntn}
                  onChange={(e) => handleChange('ntn', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. 3198765-1"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>STRN (Sales Tax Reg #)</label>
                <input
                  type="text"
                  value={form.strn}
                  onChange={(e) => handleChange('strn', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. 1700198432"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Terms (days)</label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={form.payment_terms_days}
                  onChange={(e) => handleChange('payment_terms_days', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. 30"
                />
              </div>
            </div>

            {/* Active toggle */}
            <div style={{ ...styles.formGroup, marginTop: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>
                  Active (uncheck to disable this vendor without deleting)
                </span>
              </label>
            </div>

          </div>

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" style={styles.submitButton} disabled={submitting}>
              {submitting ? '⏳ Saving...' : (isEdit ? '💾 Update Vendor' : '💾 Create Vendor')}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

const styles = {
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    background: 'white',
    borderRadius: '12px',
    width: '95%',
    maxWidth: '820px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '20px 30px',
    borderBottom: '2px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    position: 'sticky', top: 0, background: 'white', zIndex: 10,
  },
  modalTitle: {
    fontSize: '22px', fontWeight: '700',
    color: '#2c3e50', margin: 0,
  },
  closeButton: {
    padding: '8px 16px',
    background: '#f0f0f0', border: 'none',
    borderRadius: '8px', cursor: 'pointer',
    fontSize: '20px', fontWeight: '700', color: '#666',
  },
  sectionLabel: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#7f8c8d',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginTop: '16px',
    marginBottom: '12px',
    paddingBottom: '6px',
    borderBottom: '1px solid #f0f0f0',
  },
  row: {
    display: 'flex', gap: '16px', flexWrap: 'wrap',
  },
  formGroup: {
    flex: 1, minWidth: '180px',
    display: 'flex', flexDirection: 'column',
    gap: '6px', marginBottom: '12px',
  },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  required: { color: '#e74c3c' },
  input: {
    width: '100%', padding: '10px 12px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  modalFooter: {
    padding: '16px 30px',
    borderTop: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'flex-end', gap: '12px',
    position: 'sticky', bottom: 0, background: 'white',
  },
  cancelButton: {
    padding: '12px 24px',
    background: '#95a5a6', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
  submitButton: {
    padding: '12px 28px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
};

export default VendorFormModal;